import { useEffect, useState } from "react";
import ERPAnalyticsHeader from "../components/ERPAnalyticsHeader";
import { api, formatErr } from "../lib/api";
import { useAuth, canAtLeast } from "../lib/auth";
import { toast } from "sonner";
import {
  Plus, Database, CheckCircle2, XCircle, Loader2, X, Trash2, Pencil, AlertTriangle, BookOpen
} from "lucide-react";

const SOURCE_TYPES = [
  { key: "mysql", label: "MySQL / MariaDB", defaultPort: 3306, working: true },
  { key: "postgres", label: "PostgreSQL", defaultPort: 5432, working: true },
  { key: "sqlserver", label: "SQL Server", defaultPort: 1433, working: false },
  { key: "csv", label: "CSV Upload", working: false },
  { key: "excel", label: "Excel Upload", working: false },
  { key: "rest", label: "REST API (GET)", working: false },
];

const STATUS_PILL = {
  ok: { cls: "success", label: "Connected" },
  failed: { cls: "fail", label: "Failed" },
  untested: { cls: "", label: "Untested" },
};

export default function DataSources() {
  const { user } = useAuth();
  const [conns, setConns] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [discoveringId, setDiscoveringId] = useState(null);
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [c, w] = await Promise.all([api.get("/connections"), api.get("/workspaces")]);
      setConns(c.data.connections || []);
      setWorkspaces(w.data.workspaces || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const wsName = (id) => (workspaces.find((w) => w.id === id) || {}).name || id;

  const test = async (id) => {
    try {
      const r = await api.post(`/connections/${id}/test`);
      if (r.data.ok) toast.success(`Connected — ${r.data.server || r.data.server_version || "ok"} (${r.data.latency_ms || 0}ms)`);
      else toast.error(r.data.message || "Connection failed");
      load();
    } catch (e) {
      // Don't mark the connection bad just because of a proxy hiccup.
      let msg = formatErr(e);
      if (!e?.response?.data || (typeof e?.response?.data === "string" && e.response.data.includes("Cloudflare"))) {
        msg = "The analytics server did not complete the request. Your saved database connection has not been changed. Please retry.";
      }
      toast.error(msg);
    }
  };

  const discover = async (id) => {
    setDiscoveringId(id); setSchema(null);
    try {
      // Fast first pass: tables only.
      const r = await api.get(`/connections/${id}/schema?tables_only=true`);
      setSchema({ id, conn_id: id, ...r.data, _columnsByTable: {} });
      const tableCount = (r.data.schemas || []).reduce((sum, s) => sum + (s.tables?.length || 0), 0);
      toast.success(`Discovered ${tableCount} tables. Click a table to load its columns.`);
    } catch (e) {
      let msg = formatErr(e);
      if (!e?.response?.data || (typeof e?.response?.data === "string" && e.response.data.includes("Cloudflare"))) {
        msg = "The analytics server did not complete the schema request. Please retry.";
      }
      toast.error(msg);
    }
    finally { setDiscoveringId(null); }
  };

  const loadColumnsFor = async (tableName, schemaName) => {
    if (!schema) return;
    if (schema._columnsByTable?.[tableName]) return; // already loaded
    try {
      const r = await api.get(`/connections/${schema.conn_id}/tables/${encodeURIComponent(tableName)}/columns${schemaName ? `?schema=${encodeURIComponent(schemaName)}` : ""}`);
      setSchema((s) => ({ ...s, _columnsByTable: { ...(s._columnsByTable || {}), [tableName]: r.data.columns || [] } }));
    } catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (id, name) => {
    if (!confirm(`Delete connection "${name}"? This cannot be undone.`)) return;
    try { await api.delete(`/connections/${id}`); toast.success("Connection deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const togglePause = async (c) => {
    const newState = !(c.is_active !== false); // false if currently active, true if paused
    try {
      await api.post(`/connections/${c.id}/toggle`, { is_active: newState });
      toast.success(newState ? "Connection enabled" : "Connection paused for client demo");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="erp-home-shell">
      <ERPAnalyticsHeader />

      <main className="erp-content">
        <div className="erp-page-title-row">
          <div>
            <h1>Data Sources</h1>
            <p>Database connections — credentials encrypted at rest, read-only enforced.</p>
          </div>

          {canAtLeast(user, "analyst") && (
            <button className="erp-primary-btn" onClick={() => setShowCreate(true)} data-testid="ds-add">
              <Plus size={14} strokeWidth={1.8} /> New connection
            </button>
          )}
        </div>

        <div className="space-y-4">
          <div className="card-flat p-4">
            <div className="section-title mb-1">Supported sources</div>
            <div className="section-sub mb-3">Phase 1 connectors. CSV / Excel / REST / SQL Server arrive in Phase 2.</div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
              {SOURCE_TYPES.map((s) => (
                <div key={s.key} className="border rounded p-3 text-[12.5px]" style={{ borderColor: "var(--c-border)" }}>
                  <div className="font-medium">{s.label}</div>
                  <div className="mt-1">{s.working ? <span className="pill success">Available</span> : <span className="pill">Phase 2</span>}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="card-flat p-4">
            <div className="section-title mb-3">Your connections</div>
            {loading ? <div className="text-[12.5px]" style={{ color: "var(--c-text-muted)" }}>Loading…</div> : (
              <table className="tbl" data-testid="connections-table">
                <thead><tr>
                  <th>Name</th><th>Type</th><th>Host</th><th>Database</th><th>Workspace</th><th>Status</th><th>Last tested</th><th></th>
                </tr></thead>
                <tbody>
                  {conns.map((c) => {
                    const sp = STATUS_PILL[c.status] || STATUS_PILL.untested;
                    return (
                      <tr key={c.id} data-testid={`conn-${c.id}`}>
                        <td>
                          <Database size={14} strokeWidth={1.7} className="inline mr-1.5" />
                          <span className="font-medium">{c.name}</span>
                          {c.source_type === "demo" && <span className="pill warn ml-2">Demo</span>}
                          {c.is_active === false && <span className="pill ml-2" style={{ background: "#e5e7eb", color: "#4b5563" }}>Paused</span>}
                        </td>
                        <td><span className="pill">{c.source_type}</span></td>
                        <td className="font-mono text-[12px]">{c.host ? `${c.host}:${c.port || ""}` : "—"}</td>
                        <td className="font-mono text-[12px]">{c.database || "—"}</td>
                        <td><span className="pill info">{wsName(c.workspace_id)}</span></td>
                        <td><span className={`pill ${sp.cls}`}>{c.status === "ok" ? <CheckCircle2 size={11} strokeWidth={1.8} className="inline mr-1" /> : c.status === "failed" ? <XCircle size={11} strokeWidth={1.8} className="inline mr-1" /> : null}{sp.label}</span></td>
                        <td className="numeric text-[11.5px]" style={{ color: "var(--c-text-muted)" }}>{c.last_tested_at ? new Date(c.last_tested_at).toLocaleString() : "—"}</td>
                        <td className="text-right space-x-1 whitespace-nowrap">
                          <button className="btn btn-outline" onClick={() => test(c.id)} data-testid={`conn-test-${c.id}`}>Test</button>
                          <button className="btn btn-outline" onClick={() => discover(c.id)} data-testid={`conn-schema-${c.id}`}>{discoveringId === c.id ? <Loader2 size={12} className="animate-spin" /> : <BookOpen size={12} strokeWidth={1.8} />} Schema</button>
                          {user?.role === "super_admin" && (
                            <button
                              className="btn btn-ghost"
                              title={c.is_active === false ? "Enable" : "Pause / hide for client demo"}
                              onClick={() => togglePause(c)}
                              data-testid={`conn-toggle-${c.id}`}
                            >
                              {c.is_active === false ? "Enable" : "Pause"}
                            </button>
                          )}
                          {c.source_type !== "demo" && canAtLeast(user, "analyst") && (
                            <button className="btn btn-ghost" title="Edit" onClick={() => setEditing(c)} data-testid={`conn-edit-${c.id}`}><Pencil size={13} strokeWidth={1.7} /></button>
                          )}
                          {c.source_type !== "demo" && canAtLeast(user, "workspace_admin") && (
                            <button className="btn btn-ghost" title="Delete" onClick={() => remove(c.id, c.name)} data-testid={`conn-del-${c.id}`}><Trash2 size={13} strokeWidth={1.7} /></button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {conns.length === 0 && <tr><td colSpan={8} className="text-center py-6" style={{ color: "var(--c-text-muted)" }}>No connections yet.</td></tr>}
                </tbody>
              </table>
            )}
          </div>

          {schema && (
            <div className="card-flat p-4" data-testid="schema-panel">
              <div className="flex items-center justify-between mb-3">
                <div className="section-title">Discovered schema · <span className="font-mono text-[14px]" style={{ color: "var(--c-text-muted)" }}>{schema.id}</span></div>
                <button className="btn btn-ghost" onClick={() => setSchema(null)}><X size={14} strokeWidth={1.8} /></button>
              </div>
              <div className="space-y-3 max-h-[420px] overflow-auto">
                {(schema.schemas || []).map((sc) => (
                  <div key={sc.name}>
                    <div className="font-medium mb-1 text-[13px]">{sc.name} <span className="text-[11px]" style={{ color: "var(--c-text-muted)" }}>· {sc.tables.length} tables</span></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {sc.tables.map((t) => {
                        const loadedCols = schema._columnsByTable?.[t.name];
                        const cols = loadedCols || t.columns || null;
                        return (
                          <details key={t.name} className="border rounded p-2 text-[12px]" style={{ borderColor: "var(--c-border)" }} onToggle={(e) => { if (e.target.open && !cols) loadColumnsFor(t.name, sc.name); }}>
                            <summary className="cursor-pointer font-medium font-mono">{t.name} <span className="text-[10.5px]" style={{ color: "var(--c-text-muted)" }}>{cols ? `(${cols.length} cols)` : "(click to load)"}</span></summary>
                            {cols ? (
                              <ul className="mt-1.5 space-y-0.5">
                                {cols.map((c) => (
                                  <li key={c.name} className="font-mono text-[11.5px] flex justify-between">
                                    <span>{c.name}</span><span style={{ color: "var(--c-text-muted)" }}>{c.data_type}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div className="mt-1.5 text-[11px] flex items-center gap-1.5" style={{ color: "var(--c-text-muted)" }}>
                                <Loader2 size={11} className="animate-spin" /> Loading columns…
                              </div>
                            )}
                          </details>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {showCreate && <ConnectionModal mode="create" onClose={() => { setShowCreate(false); load(); }} user={user} workspaces={workspaces} />}
        {editing && <ConnectionModal mode="edit" existing={editing} onClose={() => { setEditing(null); load(); }} user={user} workspaces={workspaces} />}
      </main>
    </div>
  );
}

function ConnectionModal({ mode, existing, onClose, user, workspaces }) {
  const isEdit = mode === "edit";
  const isSuperAdmin = user?.role === "super_admin";
  const userWorkspaceId = user?.workspace_id;
  const camarooWs = workspaces.find((w) => w.slug === "camaroo");

  const [form, setForm] = useState({
    name: existing?.name || "",
    source_type: existing?.source_type || "mysql",
    host: existing?.host || "",
    port: existing?.port || 3306,
    database: existing?.database || "",
    username: existing?.username || "",
    password: "",
    ssl_mode: existing?.ssl_mode || "",
    workspace_id: existing?.workspace_id || (isSuperAdmin ? (camarooWs?.id || workspaces[0]?.id || "") : userWorkspaceId),
    read_only: true,
  });
  const [testing, setTesting] = useState(false);
  const [testRes, setTestRes] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const update = (p) => { setForm((s) => ({ ...s, ...p })); setError(""); };

  const onTypeChange = (t) => {
    const def = SOURCE_TYPES.find((x) => x.key === t);
    update({ source_type: t, port: def?.defaultPort || 0 });
  };

  const noWorkspace = !form.workspace_id;

  const test = async () => {
    setTesting(true); setTestRes(null); setError("");
    try {
      let r;
      // Edit mode with no new password → re-use stored creds via the saved-connection endpoint.
      // This avoids sending an empty password to the DB (which throttles bad-auth and would
      // exceed the proxy timeout).
      if (isEdit && !form.password) {
        r = await api.post(`/connections/${existing.id}/test`, {
          host: form.host || undefined,
          port: form.port || undefined,
          database: form.database || undefined,
          username: form.username || undefined,
          ssl_mode: form.ssl_mode || undefined,
        });
      } else {
        if (!form.password) {
          setTesting(false);
          setError("Password is required when testing a new connection.");
          toast.error("Password is required to test a new connection.");
          return;
        }
        r = await api.post("/connections/test-inline", {
          source_type: form.source_type,
          host: form.host, port: form.port, database: form.database,
          username: form.username, password: form.password, ssl_mode: form.ssl_mode || undefined,
        });
      }
      setTestRes(r.data);
      if (r.data.ok) toast.success(`Connected — ${r.data.server || r.data.server_version || "ok"} (${r.data.latency_ms || 0}ms)`);
      else toast.error(r.data.message || "Connection failed");
    } catch (e) {
      // Detect non-JSON / Cloudflare timeout responses and replace with a friendly message.
      let msg = formatErr(e);
      if (!e?.response?.data || typeof e.response.data === "string" && e.response.data.includes("Cloudflare")) {
        msg = "The analytics server did not complete the request. Your saved database connection has not been changed. Please retry.";
      }
      setTestRes({ ok: false, success: false, message: msg, error_code: e?.response?.status ? `HTTP_${e.response.status}` : "NETWORK" });
      toast.error(msg);
    } finally { setTesting(false); }
  };

  const save = async () => {
    setError("");
    if (!form.name?.trim()) { setError("Connection name is required."); return; }
    if (!form.workspace_id) { setError("No workspace assigned to this account. Please assign a workspace before creating a data source."); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/connections/${existing.id}`, {
          name: form.name, host: form.host, port: form.port, database: form.database,
          username: form.username, password: form.password || undefined, ssl_mode: form.ssl_mode || undefined,
        });
        toast.success("Connection updated");
      } else {
        await api.post("/connections", form);
        toast.success("Connection saved");
      }
      onClose();
    } catch (e) {
      const msg = formatErr(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-30" data-testid="ds-modal">
      <div className="bg-white w-[580px] max-w-[95vw] rounded-md border" style={{ borderColor: "var(--c-border)" }}>
        <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--c-border)" }}>
          <div>
            <div className="section-title">{isEdit ? `Edit · ${existing.name}` : "New connection"}</div>
            <div className="section-sub">Credentials encrypted at rest · read-only enforced.</div>
          </div>
          <button className="btn btn-ghost" onClick={onClose}><X size={14} strokeWidth={1.8} /></button>
        </div>
        <div className="p-5 space-y-3">
          {noWorkspace && (
            <div className="text-[12.5px] px-3 py-2 rounded flex items-center gap-2" style={{ background: "#fef3c7", color: "#92400e", border: "1px solid #fde68a" }} data-testid="ds-no-workspace">
              <AlertTriangle size={14} strokeWidth={1.8} />
              No workspace is assigned to this account. Please assign a workspace before creating a data source.
            </div>
          )}

          {isSuperAdmin && !isEdit && (
            <div>
              <label className="label">Workspace</label>
              <select className="select" value={form.workspace_id} onChange={(e) => update({ workspace_id: e.target.value })} data-testid="ds-workspace">
                <option value="">— pick workspace —</option>
                {workspaces.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
          )}
          {!isSuperAdmin && form.workspace_id && (
            <div className="text-[12px]" style={{ color: "var(--c-text-muted)" }}>
              Workspace: <span className="font-medium" style={{ color: "var(--c-text)" }}>{workspaces.find((w) => w.id === form.workspace_id)?.name || form.workspace_id}</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Connection name</label>
              <input className="input" value={form.name} onChange={(e) => update({ name: e.target.value })} data-testid="ds-name" placeholder="e.g. Camaroo QA - DFL" />
            </div>
            <div>
              <label className="label">Type</label>
              <select className="select" value={form.source_type} onChange={(e) => onTypeChange(e.target.value)} disabled={isEdit} data-testid="ds-type">
                {SOURCE_TYPES.filter((s) => s.working).map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="label">Host</label>
              <input className="input" value={form.host} onChange={(e) => update({ host: e.target.value })} data-testid="ds-host" placeholder="e.g. 168.231.102.34" />
            </div>
            <div>
              <label className="label">Port</label>
              <input className="input" type="number" value={form.port} onChange={(e) => update({ port: Number(e.target.value) })} data-testid="ds-port" />
            </div>
          </div>
          <div>
            <label className="label">Database</label>
            <input className="input" value={form.database} onChange={(e) => update({ database: e.target.value })} data-testid="ds-db" placeholder="e.g. qa_dfl_v3_1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Username</label>
              <input className="input" value={form.username} onChange={(e) => update({ username: e.target.value })} data-testid="ds-user" placeholder="e.g. dev_team_ro" />
            </div>
            <div>
              <label className="label">Password {isEdit && "(leave blank to keep)"}</label>
              <input className="input" type="password" value={form.password} onChange={(e) => update({ password: e.target.value })} data-testid="ds-pass" autoComplete="new-password" />
            </div>
          </div>
          <div>
            <label className="label">SSL mode</label>
            <select className="select" value={form.ssl_mode} onChange={(e) => update({ ssl_mode: e.target.value })} data-testid="ds-ssl">
              <option value="">(driver default)</option>
              <option value="disabled">disabled</option>
              <option value="preferred">preferred</option>
              <option value="required">required</option>
            </select>
          </div>

          {error && (
            <div className="text-[12.5px] px-3 py-2 rounded" style={{ background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca" }} data-testid="ds-error">
              {error}
            </div>
          )}
          {testRes && (
            <div className={`text-[12.5px] px-3 py-2 rounded ${testRes.ok ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`} data-testid="ds-test-result">
              {testRes.ok ? `Connected — ${testRes.server_version || "ok"} (${testRes.latency_ms || 0}ms)` : `Failed: ${testRes.message}`}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t flex justify-between items-center" style={{ borderColor: "var(--c-border)" }}>
          <button className="btn btn-outline" onClick={test} disabled={testing} data-testid="ds-test">
            {testing ? <Loader2 size={13} className="animate-spin" /> : null} Test connection
          </button>
          <div className="flex gap-2">
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving || !form.name || noWorkspace} data-testid="ds-save">
              {saving ? <><Loader2 size={13} className="animate-spin" /> Saving…</> : (isEdit ? "Save changes" : "Save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
